import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
  Router,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from '../../services/auth-service/auth.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ApiUrlHelper } from '../../common/api-url-helper';
import { CommonService } from '../../services/common/common.service';
import { RoleRightsService } from '../../services/common/role-rights.service';
import { SharedService } from '../../services/shared-service/shared.service';

interface MenuItem {
  id: number;
  name: string;
  icon: string;
  url: string;
  isParent: boolean;
  parentId: number;
  isForm: boolean;
  formId: number;
  subMenus?: MenuItem[];
  subItems?: MenuItem[];
  isOpen?: boolean;
  isNotePending:boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  menuItems: MenuItem[] = [];

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly commonService: CommonService,
    private readonly spinner: NgxSpinnerService,
    private readonly apiUrl: ApiUrlHelper,
    private readonly roleRightsService: RoleRightsService,
    private readonly shareService: SharedService,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | boolean
    | UrlTree
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree> {
    if (!this.authService.isLoggedIn()) {
      this.router.navigate(['/login']); // go to login if not authenticated
      return false;
    }

    return this.fetchMenuList().pipe(
      map(() => {
        const url = state.url;
        if (this.isAccessible(url)) {
          return true;
        } else {
          this.router.navigate(['no-access']); // Redirect to a no-access page or any other page;
          return false;
        }
      }),
      catchError(() => {
        this.router.navigate(['/error']); // Redirect to an error page
        return of(false);
      })
    );
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | boolean
    | UrlTree
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree> {
    return this.canActivate(childRoute, state); // Reuse canActivate logic
  }

  fetchMenuList(): Observable<void> {
    this.roleRightsService.dataFetched = false;
    const api = this.apiUrl.apiUrl.MenuList.GetMenuList;
    this.spinner.show();
    return this.commonService.doGet(api).pipe(
      map((response: any) => {
        if (response.success) {
          this.menuItems = this.parseMenuItems(response.data);
          this.shareService.setIsNotePending(this.menuItems[0].isNotePending)  
          const newMenuItem2: any = {
            id: -4, // Ensure this ID is unique
            name: 'ActivityLogs',
            icon: '3.png',
            url: '/activity-log',
          };
          // this.menuItems.push(newMenuItem);
          this.menuItems.push(newMenuItem2);
          this.spinner.hide();
        } else {
          this.spinner.hide();
          return;
        }
      }),
      catchError((err) => {
        console.error('Error fetching menu list:', err);
        this.spinner.hide();
        return of();
      })
    );
  }

  parseMenuItems(response: any[]): MenuItem[] {
    return response.map((item) => ({
      id: item.parentMenuId,
      name: item.parentMenuName,
      icon: item.icon,
      url: item.url,
      isParent: item.items !== null,
      parentId: 0,
      isForm: false,
      formId: 0,
      subMenus: item.items ? JSON.parse(item.items) : [],
      isOpen: false,
      isNotePending:item.isNotePending
    }));
  }

  isAccessible(url: string): boolean {
    const segments = url.split('/').filter((segment) => segment); // Remove empty segments
    if (segments.length === 0) {
      return false;
    }
    let currentMenus: MenuItem[] = this.menuItems; // Initialize currentMenus
    let isMatched = false;

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      // Check for a direct match with the current level menus (parent or submenu)
      let menu = currentMenus.find(
        (m) =>
          m.url.endsWith(`/${segment}`) ||
          m.name.toLowerCase() === segment.toLowerCase()
      );
      if (menu) {
        isMatched = true;
        if (menu.isParent && menu.subMenus && menu.subMenus.length > 0) {
          // Move to the next submenu level if there are more submenus
          currentMenus = menu.subMenus;
        } else {
          // If no submenus exist but there are more segments, allow them as "extra"
          if (i < segments.length - 1) {
            // Since we've reached a valid endpoint and extra segments exist, break out of the loop
            break;
          }
        }
      } else {
        // If no match was found at this level and we still have more segments, return false
        return false;
      }
    }
    return isMatched;
  }

  flattenMenuItems(menuItems: MenuItem[]): MenuItem[] {
    let result: MenuItem[] = [];
    for (const item of menuItems) {
      result.push(item);
      if (item.subMenus) {
        result = result.concat(this.flattenMenuItems(item.subMenus));
      }
    }
    return result;
  }
}
